import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { ApiExportadorArquivosService } from 'src/app/services/api/exportador/api-exportador-arquivos.service';
import { SupplierFile, SupplierFileList, SupplierFileOrdering } from 'src/app/models/api/exportador/supplier-file';
import { IPaginator } from 'src/app/ui/paginator/paginator.component';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { ToastService } from 'src/app/ui/toast/toast.service';
import { ToastType } from 'src/app/ui/toast/toast.model';
import { SupplierFilePreviewModalComponent } from './supplier-file-preview-modal/supplier-file-preview-modal.component';
import { SupplierFilesPeriodModalComponent } from './supplier-files-period-modal/supplier-files-period-modal.component';
import { mensagemDeErro } from './utils/error-message';

interface OpcaoOrdenacao extends SupplierFileOrdering {
  descricao: string;
}

@Component({
  selector: 'c-supplier-files',
  templateUrl: './supplier-files.component.html',
  styleUrls: ['./supplier-files.component.scss']
})
export class SupplierFilesComponent implements OnInit, OnDestroy {

  /**
   * A rota de previa carrega o arquivo inteiro em memoria como texto.
   * Acima disso a tela oferece so o download.
   */
  private static readonly tamanhoMaximoPrevia = 5 * 1024 * 1024;

  /** Janela padrao ao abrir a tela ou trocar de fornecedor. */
  private static readonly diasIntervaloPadrao = 14;

  /** Teto do backend para o intervalo entre dataInicio e dataFim (inclusive). */
  private static readonly maxIntervaloDias = 30;

  readonly ordenacoes: OpcaoOrdenacao[] = [
    { descricao: 'mais recentes primeiro', orderBy: 'created', order: 'desc' },
    { descricao: 'mais antigos primeiro', orderBy: 'created', order: 'asc' },
    { descricao: 'nome (A-Z)', orderBy: 'name', order: 'asc' },
    { descricao: 'nome (Z-A)', orderBy: 'name', order: 'desc' }
  ];

  fornecedores: string[] = [];
  fornecedoresCarregados = false;
  codigoSap: string;
  ordenacao = this.ordenacoes[0];

  resumo: SupplierFileList;
  arquivos: SupplierFile[] = [];
  filtrados: SupplierFile[] = [];
  visiveis: SupplierFile[] = [];
  selecionados = new Set<string>();

  busca = '';
  dataInicio = moment().subtract(SupplierFilesComponent.diasIntervaloPadrao, 'days').format('YYYY-MM-DD');
  dataFim = moment().format('YYYY-MM-DD');
  paginacao: IPaginator = { total: 0, page: 1, pageLength: 25, isLastPage: true };

  private busca$ = new Subject<string>();
  private destruido$ = new Subject<void>();

  constructor(
    private api: ApiExportadorArquivosService,
    private modalService: ModalService,
    private toastService: ToastService
  ) { }

  /** Fornecedor recebe um unico codigo da api; funcionario recebe a lista toda. */
  get temSeletorDeFornecedor() {
    return this.fornecedores.length > 1;
  }

  get todosSelecionados() {
    return this.filtrados.length > 0 && this.selecionados.size === this.filtrados.length;
  }

  get temFiltrosAtivos() {
    const padrao = this.periodoPadrao();
    return !!this.busca
      || this.ordenacao !== this.ordenacoes[0]
      || this.dataInicio !== padrao.inicio
      || this.dataFim !== padrao.fim;
  }

  ngOnInit() {
    // O campo reflete o que foi digitado na hora; so o recorte da lista espera
    this.busca$
      .pipe(debounceTime(300), takeUntil(this.destruido$))
      .subscribe(() => {
        this.paginacao = { ...this.paginacao, page: 1 };
        this.aplicarFiltro();
      });

    this.carregarFornecedores();
  }

  ngOnDestroy() {
    this.destruido$.next();
    this.destruido$.complete();
  }

  selecionarFornecedor(codigoSap: string) {
    this.codigoSap = codigoSap;
    this.carregarArquivos();
  }

  alterarOrdenacao(ordenacao: OpcaoOrdenacao) {
    this.ordenacao = ordenacao;
    this.carregarArquivos();
  }

  alterarPeriodo(dataInicio: string, dataFim: string) {
    if (!dataInicio || !dataFim) { return; }

    if (moment(dataFim).isBefore(dataInicio, 'day')) {
      this.toastService.toast('a data final não pode ser anterior à data inicial.', ToastType.warning);
      return;
    }

    const dias = moment(dataFim).diff(moment(dataInicio), 'days');
    if (dias > SupplierFilesComponent.maxIntervaloDias) {
      this.toastService.toast(`o intervalo não pode passar de ${SupplierFilesComponent.maxIntervaloDias} dias.`, ToastType.warning);
      return;
    }

    this.dataInicio = dataInicio;
    this.dataFim = dataFim;
    this.carregarArquivos();
  }

  abrirCalendario() {
    const ref = this.modalService.open(SupplierFilesPeriodModalComponent, {
      initialState: { type: 'dialog' },
      contentInitialState: {
        dataInicio: this.dataInicio,
        dataFim: this.dataFim,
        maxIntervaloDias: SupplierFilesComponent.maxIntervaloDias
      }
    });

    ref.contentComponentRef.instance.periodoSelecionado
      .pipe(take(1))
      .subscribe(({ dataInicio, dataFim }) => this.alterarPeriodo(dataInicio, dataFim));
  }

  recarregarBusca() {
    if (!this.codigoSap) { return; }
    this.carregarArquivos();
  }

  limparFiltros() {
    const padrao = this.periodoPadrao();
    this.busca = '';
    this.ordenacao = this.ordenacoes[0];
    this.dataInicio = padrao.inicio;
    this.dataFim = padrao.fim;

    if (this.codigoSap) {
      this.carregarArquivos();
    }
  }

  filtrar(termo: string) {
    this.busca = termo || '';
    this.busca$.next(this.busca);
  }

  paginar(paginacao: IPaginator) {
    this.paginacao = paginacao;
    this.aplicarPaginacao();
  }

  alternarSelecao(arquivo: SupplierFile) {
    if (this.selecionados.has(arquivo.path)) {
      this.selecionados.delete(arquivo.path);
    } else {
      this.selecionados.add(arquivo.path);
    }
  }

  /** Vale para a lista filtrada inteira, nao apenas para a pagina visivel. */
  alternarTodos() {
    if (this.todosSelecionados) {
      this.selecionados.clear();
    } else {
      this.filtrados.forEach(arquivo => this.selecionados.add(arquivo.path));
    }
  }

  baixar(arquivo: SupplierFile) {
    this.api.downloadFile(this.codigoSap, arquivo.path).subscribe(
      blob => saveAs(blob, arquivo.fileName),
      erro => this.erro(erro, 'download')
    );
  }

  baixarTodos() {
    this.api.downloadAllZip(this.codigoSap).subscribe(
      blob => saveAs(blob, `${this.codigoSap}.zip`),
      erro => this.erro(erro, 'zip')
    );
  }

  baixarSelecionados() {
    const paths = Array.from(this.selecionados);
    if (!paths.length) { return; }

    this.api.downloadSelectedZip(this.codigoSap, paths).subscribe(
      blob => saveAs(blob, `${this.codigoSap}_selecionados.zip`),
      erro => this.erro(erro, 'zip')
    );
  }

  visualizar(arquivo: SupplierFile) {
    if (arquivo.size > SupplierFilesComponent.tamanhoMaximoPrevia) {
      this.toastService.toast('arquivo grande demais para pré-visualizar — use o download.', ToastType.warning);
      return;
    }

    this.modalService.open(SupplierFilePreviewModalComponent, {
      contentInitialState: {
        path: arquivo.path,
        fileName: arquivo.fileName
      },
      initialState: {
        type: 'large'
      }
    });
  }

  private carregarFornecedores() {
    this.api.getFornecedores().subscribe(
      resposta => {
        this.fornecedores = (resposta && resposta.fornecedores) || [];
        this.fornecedoresCarregados = true;

        // Fornecedor logado so tem o proprio codigo: nao ha o que escolher
        if (this.fornecedores.length === 1) {
          this.selecionarFornecedor(this.fornecedores[0]);
        }
      },
      erro => {
        this.fornecedoresCarregados = true;
        this.erro(erro, 'fornecedores');
      }
    );
  }

  private carregarArquivos() {
    if (!this.codigoSap) { return; }

    const createdAfter = moment(this.dataInicio).startOf('day').toISOString();
    const createdBefore = moment(this.dataFim).endOf('day').toISOString();

    this.api.listFiles(this.codigoSap, this.ordenacao.orderBy, this.ordenacao.order, createdAfter, createdBefore).subscribe(
      resposta => {
        this.resumo = resposta;
        this.arquivos = (resposta && resposta.files) || [];
        this.selecionados.clear();
        this.paginacao = { ...this.paginacao, page: 1 };
        this.aplicarFiltro();
      },
      erro => {
        this.resumo = null;
        this.arquivos = [];
        this.selecionados.clear();
        this.aplicarFiltro();
        this.erro(erro, 'listagem');
      }
    );
  }

  /** A api nao filtra nem pagina: devolve a lista completa e a tela recorta. */
  private aplicarFiltro() {
    const termo = this.busca.trim().toLowerCase();

    this.filtrados = termo
      ? this.arquivos.filter(arquivo => arquivo.fileName.toLowerCase().includes(termo))
      : this.arquivos;

    // Seleção fora do filtro atual deixaria o usuário baixando o que não vê
    this.selecionados.forEach(path => {
      if (!this.filtrados.some(arquivo => arquivo.path === path)) {
        this.selecionados.delete(path);
      }
    });

    this.aplicarPaginacao();
  }

  private aplicarPaginacao() {
    const inicio = (this.paginacao.page - 1) * this.paginacao.pageLength;
    this.visiveis = this.filtrados.slice(inicio, inicio + this.paginacao.pageLength);
  }

  private erro(erro: any, contexto: 'fornecedores' | 'listagem' | 'download' | 'zip') {
    this.toastService.toast(mensagemDeErro(erro, contexto), ToastType.error);
  }

  private periodoPadrao() {
    return {
      inicio: moment().subtract(SupplierFilesComponent.diasIntervaloPadrao, 'days').format('YYYY-MM-DD'),
      fim: moment().format('YYYY-MM-DD')
    };
  }

}
